import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { 
  Smartphone, 
  Wifi, 
  QrCode, 
  Users, 
  Globe, 
  ArrowRight, 
  CheckCircle2, 
  FileText,
  Target,
  Eye,
  ChevronRight,
  Layers
} from "lucide-react";
import nfcHeroMockup from "@/assets/nfc-hero-mockup.png";

const NFCBusinessCard = () => {
  const [activeFeature, setActiveFeature] = useState(0);


  const features = [
    {
      icon: Smartphone,
      title: "Tap to Contact",
      description: "Recipients can tap any item on your profile to contact you via call, email, SMS, or instant messaging apps. The more ways you make yourself available, the more likely customers reach out.",
      video: "/assets/tap-to-contact-nfc.mp4"
    },
    {
      icon: FileText,
      title: "Rich Profile Content",
      description: "Share videos about your products, PDF price lists, articles about your brand, podcasts you've appeared on — all accessible with one tap from your QCard profile.",
      video: "/assets/rich-profile-content.mp4"
    },
    {
      icon: Users,
      title: "Colleagues",
      description: "Your QCard profile can show other team members and provide access to their profiles. One scan gives access to your entire team's digital business cards.",
      video: "/assets/colleagues-directory.mp4"
    }
  ];

  const materials = [
    {
      title: "PVC (Plastic)",
      description: "Lightweight, durable, and long-lasting. The standard format used in most professional NFC cards."
    }
  ];

  const compatibility = [
    "Works with most Android phones",
    "Works with iPhones (model 7 or newer)",
    "Includes printed QR code as backup option"
  ];

  const keyTakeaways = [
    "No app needed to receive contact info",
    "Tap to share your digital profile instantly",
    "One card can be used repeatedly forever",
    "Materials designed for daily business use",
    "Ideal for meetings, events, and client visits"
  ];

  const digitalMarketingFeatures = [
    {
      icon: Target,
      title: "Retargeted Advertising",
      description: "A web cookie can be dropped on customers who view your QCard profile, enabling retargeting ads on other websites they visit."
    },
    {
      icon: Eye,
      title: "Analytics",
      description: "Track profile visits, contact additions, and gain insights into the channels through which your QCard is accessed."
    },
    {
      icon: Globe,
      title: "Website Integration",
      description: "Collaborate with the QCard team to integrate QCard into your website and customize it based on your needs."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative pt-12 sm:pt-16 pb-16 sm:pb-24 overflow-hidden min-h-[600px] lg:min-h-[700px]">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-secondary/50 via-background to-accent/30" />
        <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-primary/10 rounded-full blur-3xl" />
        
        {/* Hero image - positioned absolutely on larger screens */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2 hidden lg:block pointer-events-none">
          <img 
            src={nfcHeroMockup} 
            alt="QCard NFC Business Card with phone mockup" 
            className="w-[700px] xl:w-[800px] h-auto object-contain translate-x-[10%]"
          />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left content */}
            <div className="space-y-8 relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#8544d8]/10 text-[#8544d8] text-sm font-medium">
                <Wifi className="w-4 h-4" />
                NFC Technology
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight">
                QCard <span className="text-gradient-purple">NFC</span> Business Card
              </h1>
              
              <p className="text-lg sm:text-xl text-muted-foreground max-w-xl">
                Share your contact details using your custom NFC-enabled business card. Just tap to connect — no apps, no QR scanning, no typing needed.
              </p>
            </div>

            {/* Mobile image - shown only on smaller screens */}
            <div className="relative flex justify-center lg:hidden">
              <img 
                src={nfcHeroMockup} 
                alt="QCard NFC Business Card with phone mockup" 
                className="w-full max-w-[500px] h-auto object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What is NFC Section */}
      <section className="py-16 sm:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            <div className="space-y-6">
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
                What is an NFC Business Card?
              </h2>
              <div className="space-y-4 text-muted-foreground text-lg leading-relaxed">
                <p>
                  An NFC business card is a physical card with a small, embedded chip that lets you share your contact details or online profile by <strong className="text-foreground">tapping the card against a smartphone</strong>.
                </p>
                <p>
                  There's no app to install, no QR scanning, and no typing needed. When the card is tapped, it sends a link to the other person's phone, usually opening a digital profile, contact page, or company website.
                </p>
                <p>
                  It's a practical tool for people who want to exchange details quickly without using printed business cards.
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
                What is it made of?
              </h2>
              <div className="space-y-4 text-muted-foreground text-lg leading-relaxed">
                <p>QCard NFC business cards are made of durable materials:</p>
                <div className="p-4 bg-background rounded-xl border border-border">
                  <p className="font-semibold text-foreground">PVC (Plastic)</p>
                  <p className="text-sm mt-1">Lightweight and long-lasting. The common format used in most professional cards.</p>
                </div>
                <p>
                  Inside each card is a <strong className="text-foreground">passive NFC chip</strong>, powered by the phone that reads it. That means the card itself doesn't need a battery or charging — it works instantly when brought close to most smartphones.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Compatibility Section */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Universal Compatibility
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Works with virtually every modern smartphone
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-6 mb-12">
            <div className="flex items-center gap-3 px-6 py-4 bg-muted/50 rounded-2xl border border-border">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.523 2.016c1.969 0 3.965 1.628 3.965 4.47 0 4.784-6.844 15.498-9.488 15.498-2.644 0-9.488-10.714-9.488-15.498 0-2.842 1.996-4.47 3.965-4.47 1.236 0 3.393 1.095 5.523 1.095 2.13 0 4.287-1.095 5.523-1.095z"/>
                </svg>
              </div>
              <div>
                <p className="font-semibold text-foreground">Android</p>
                <p className="text-sm text-muted-foreground">Most devices</p>
              </div>
            </div>

            <div className="flex items-center gap-3 px-6 py-4 bg-muted/50 rounded-2xl border border-border">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center">
                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
              </div>
              <div>
                <p className="font-semibold text-foreground">iPhone</p>
                <p className="text-sm text-muted-foreground">Model 7 or newer</p>
              </div>
            </div>

            <div className="flex items-center gap-3 px-6 py-4 bg-muted/50 rounded-2xl border border-border">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#8544d8] to-[#a855f7] flex items-center justify-center">
                <QrCode className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-semibold text-foreground">QR Backup</p>
                <p className="text-sm text-muted-foreground">Printed on card</p>
              </div>
            </div>
          </div>

          {/* Key Takeaways */}
          <div className="max-w-3xl mx-auto">
            <h3 className="text-xl font-bold text-foreground mb-6 text-center">Key Takeaways</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {keyTakeaways.map((takeaway, index) => (
                <div key={index} className="flex items-start gap-3 p-4 bg-muted/30 rounded-xl">
                  <CheckCircle2 className="w-5 h-5 text-[#8544d8] flex-shrink-0 mt-0.5" />
                  <span className="text-foreground">{takeaway}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-muted/50 to-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Powerful Features
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Everything you need to make meaningful connections
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Feature list */}
            <div className="space-y-4">
              {features.map((feature, index) => (
                <button
                  key={index}
                  onClick={() => setActiveFeature(index)}
                  className={`w-full text-left p-6 rounded-2xl border transition-all duration-300 ${
                    activeFeature === index
                      ? "bg-[#8544d8]/5 border-[#8544d8] shadow-lg"
                      : "bg-background border-border hover:border-[#8544d8]/30"
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                      activeFeature === index
                        ? "bg-[#8544d8] text-white"
                        : "bg-muted text-muted-foreground"
                    }`}>
                      <feature.icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-foreground mb-2">{feature.title}</h3>
                      <p className={`text-sm transition-all duration-300 ${
                        activeFeature === index ? "text-muted-foreground" : "text-muted-foreground/70 line-clamp-2"
                      }`}>
                        {feature.description}
                      </p>
                    </div>
                    <ChevronRight className={`w-5 h-5 text-muted-foreground transition-transform flex-shrink-0 ${
                      activeFeature === index ? "rotate-90" : ""
                    }`} />
                  </div>
                </button>
              ))}
            </div>

            {/* Feature visual */}
            <div className="relative lg:sticky lg:top-32">
              <div className="relative bg-gradient-to-br from-[#8544d8]/5 to-accent/10 rounded-3xl p-8 sm:p-12 border border-border overflow-hidden">
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#8544d8]/10 rounded-full blur-2xl" />
                <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-accent/20 rounded-full blur-2xl" />
                
                <div className="relative">
                  {/* Video display for Tap to Contact */}
                  {features[activeFeature].video ? (
                    <div className="mb-6 rounded-xl overflow-hidden">
                      <video
                        key={features[activeFeature].video}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="w-full h-auto object-cover rounded-xl"
                      >
                        <source src={features[activeFeature].video} type="video/mp4" />
                      </video>
                    </div>
                  ) : (
                    <div className="w-16 h-16 rounded-2xl bg-[#8544d8]/10 flex items-center justify-center mb-6">
                      {(() => {
                        const Icon = features[activeFeature].icon;
                        return <Icon className="w-8 h-8 text-[#8544d8]" />;
                      })()}
                    </div>
                  )}
                  
                  <h3 className="text-2xl font-bold text-foreground mb-4">
                    {features[activeFeature].title}
                  </h3>
                  
                  <p className="text-muted-foreground text-lg leading-relaxed">
                    {features[activeFeature].description}
                  </p>

                  {/* Progress indicators */}
                  <div className="flex gap-2 mt-8">
                    {features.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setActiveFeature(index)}
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                          activeFeature === index
                            ? "bg-[#8544d8] w-8"
                            : "bg-muted-foreground/20 w-4 hover:bg-muted-foreground/40"
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Digital Marketing Integration Section */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#8544d8]/10 text-[#8544d8] text-sm font-medium mb-6">
              <Layers className="w-4 h-4" />
              Marketing Integration
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              An Essential Part of Your Digital Marketing Stack
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              QCard integrates seamlessly with your existing marketing tools
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {digitalMarketingFeatures.map((feature, index) => (
              <div 
                key={index}
                className="group relative p-8 bg-background rounded-2xl border border-border hover:border-[#8544d8]/30 hover:shadow-xl transition-all duration-300"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#8544d8]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl" />
                
                <div className="relative">
                  <div className="w-14 h-14 rounded-xl bg-[#8544d8]/10 flex items-center justify-center mb-6 group-hover:bg-[#8544d8] group-hover:text-white transition-colors">
                    <feature.icon className="w-7 h-7 text-[#8544d8] group-hover:text-white transition-colors" />
                  </div>
                  
                  <h3 className="text-xl font-bold text-foreground mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section with Sign Up Form */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-[#8544d8]/5 via-background to-accent/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
              Ready to Network with QCard?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Join thousands of professionals who have upgraded to digital business cards
            </p>
          </div>
          
          {/* Sign Up Form */}
          <div className="flex justify-center">
            <div className="bg-card rounded-2xl p-4 shadow-2xl border border-border w-full max-w-md overflow-hidden">
              <div className="flex items-center justify-center min-h-[60px] mb-4">
                <h3 className="text-xl font-bold text-center text-foreground">
                  Sign up for your<br />FREE Digital Business Card
                </h3>
              </div>
              <div className="w-full rounded-xl overflow-hidden flex justify-center touch-none" style={{
                height: "630px",
                overflow: "hidden",
                WebkitOverflowScrolling: "auto"
              }}>
                <iframe 
                  src="https://card.qcardenterprise.com/createNewUserAccount"
                  className="w-full max-w-[370px]" 
                  height="730" 
                  title="Sign Up Form" 
                  style={{
                    border: "none",
                    overflow: "hidden",
                    pointerEvents: "auto"
                  }} 
                  scrolling="no"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default NFCBusinessCard;
